"use client";
import { useEffect, useMemo } from "react";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import ProductSection from "./ProductSection";
import ProductSectionSkeleton from "@/app/components/elements/skeletons/ProductSectionSkeleton";
import { useTrackGenericView } from "@/app/hooks/useTrackView";
import type { Product } from "@/app/lib/features/product/product.types";

// Numeric section id'si olmayan öneri carousel'i — viewport'a girince
// `product_group_{key}` impression'ı atar (kullanıcının gördüğü öneri blokları).
const BottomGroupSection = ({
  sectionId,
  title,
  products,
}: {
  sectionId: string;
  title: string;
  products: Product[];
}) => {
  const trackRef = useTrackGenericView(`product_group_${sectionId}`);
  return (
    <div id={sectionId} ref={trackRef}>
      <ProductSection
        title={title}
        products={products}
        showViewAll={false}
        priorityCount={0}
      />
    </div>
  );
};

const FooterProductSections = () => {
  const {
    product,
    bottomGroupSections,
    fetchBottomGroupProducts,
    getBottomGroupProductsByKey,
    getBottomGroupStatusByKey,
  } = useProductDetail();

  const productId = product?.id;
  const sectionsLength = bottomGroupSections.length;

  const sectionKeys = useMemo(
    () => bottomGroupSections.map((s) => s.key),
    [bottomGroupSections],
  );

  useEffect(() => {
    if (!productId || sectionsLength === 0) return;

    sectionKeys.forEach((sectionKey) => {
      const status = getBottomGroupStatusByKey(sectionKey);

      if (status.status === "idle") {
        fetchBottomGroupProducts(productId, sectionKey);
      }
    });
  }, [
    productId,
    sectionsLength,
    sectionKeys,
    fetchBottomGroupProducts,
    getBottomGroupStatusByKey,
  ]);

  if (!bottomGroupSections || bottomGroupSections.length === 0) return null;

  return (
    <>
      {bottomGroupSections.map((section, index) => {
        const sectionKey = section.key;
        const sectionId = sectionKey.replace(/_/g, "-");
        const products = getBottomGroupProductsByKey(sectionKey);
        const { isSuccess } = getBottomGroupStatusByKey(sectionKey);

        // Karar VERİ YOKLUĞUNA göre veriliyor, isLoading'e göre değil.
        //
        // Eski koşul `isLoading && products.length === 0` idi ve SSR'da her bölümün
        // durumu "idle" olduğu için İKİ DAL DA tutmuyordu: ilk HTML'e 5 bölümün
        // yalnızca başlığı giriyor, yüksekliği ~0 kalıyordu. Hidrasyondan sonra
        // aşağıdaki useEffect 5 fetch açıyor ve her biri ~380px'lik carousel'e
        // dönüşüyor — footer ~1.900px aşağı kayıyor.
        //
        // Ölçüm (production prerender, script'ler çıkarılmış): 6 bölüm kabuğu var,
        // içlerinde 0 ürün kartı ve 0 skeleton. CLS 0,751'in 0,741'i buradan.
        //
        // Skeleton'ı "idle" durumunda da basmak ilk boyamada yeri ayırıyor;
        // ProductSectionSkeleton gerçek bölümle aynı yüksekliği tuttuğu için
        // içerik geldiğinde footer hiç oynamıyor.
        if (products.length === 0) {
          // İstek bitti ve bölüm gerçekten boş → tamamen kaldır.
          if (isSuccess) return null;
          return (
            <div key={index} id={sectionId}>
              <ProductSectionSkeleton />
            </div>
          );
        }
        // Not: isLoading bilerek okunmuyor — veri varken tazeleme sırasında
        // skeleton'a düşmek ürünleri ekrandan silerdi (skeleton guard deseni).

        return (
          <BottomGroupSection
            key={index}
            sectionId={sectionId}
            title={section.title}
            products={products}
          />
        );
      })}
    </>
  );
};

export default FooterProductSections;
